import { API_CONFIG } from '../config/api';

export interface HierarchyInfo {
  enterpriseId: string;
  isParent: boolean;
  isSub: boolean;
  parentEnterpriseId: string | null;
  hierarchyConfig: {
    relationshipType: 'standalone' | 'parent' | 'sub';
    hierarchyLevel: 0 | 1;
    billingModel: string;
    dateLinked: string | null;
    linkedBy: string | null;
  };
}

export interface SubEnterpriseData {
  id: string;
  name: string;
  email: string;
  dateLinked: string;
  linkedBy: string;
  totalUsers: number;
}

export interface LinkEnterpriseResponse {
  success: boolean;
  message: string;
  data: {
    subEnterpriseId: string;
    parentEnterpriseId: string;
    linkedDate: string;
    message: string;
  };
}

export interface CreateEnterpriseResponse {
  success: boolean;
  message: string;
  data: {
    subEnterpriseId: string;
    parentEnterpriseId: string;
    email: string;
    createdDate: string;
    message: string;
  };
}

/**
 * Get current enterprise hierarchy information
 */
export const getHierarchyInfo = async (token: string): Promise<HierarchyInfo> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}/api/enterprise-hierarchy/info`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to fetch hierarchy information');
  }

  const data = await response.json();
  return data.data;
};

/**
 * Get all sub-enterprises for current parent enterprise
 */
export const getSubEnterprises = async (token: string): Promise<SubEnterpriseData[]> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}/api/enterprise-hierarchy/sub-enterprises`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to fetch sub-enterprises');
  }

  const data = await response.json();
  return data.data.subEnterprises || [];
};

/**
 * Link an existing enterprise as a sub-enterprise (Scenario 1)
 */
export const linkEnterprise = async (
  token: string,
  parentEnterpriseId: string,
  subEnterpriseEmail: string
): Promise<LinkEnterpriseResponse> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}/api/enterprise-hierarchy/link-enterprise`,
    {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        parentEnterpriseId,
        subEnterpriseEmail
      })
    }
  );

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to link enterprise');
  }

  return data;
};

/**
 * Create a new sub-enterprise (Scenario 2)
 */
export const createSubEnterprise = async (
  token: string,
  parentEnterpriseId: string,
  subEnterpriseData: {
    name: string;
    email: string;
    firstName: string;
    lastName: string;
    password: string;
  }
): Promise<CreateEnterpriseResponse> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}/api/enterprise-hierarchy/create-enterprise`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        parentEnterpriseId,
        ...subEnterpriseData
      })
    }
  );

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to create sub-enterprise');
  }

  return data;
};

/**
 * Check if current user has access to target enterprise data
 */
export const checkAccessPermission = async (
  token: string,
  targetEnterpriseId: string
): Promise<{ hasAccess: boolean; reason: string }> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}/api/enterprise-hierarchy/check-access`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        targetEnterpriseId
      })
    }
  );

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to check access');
  }

  return data.data;
};

/**
 * Get visible users for current enterprise
 * Returns users from own enterprise or all sub-enterprises if parent
 */
export const getVisibleUsers = async (
  token: string,
  page: number = 1,
  limit: number = 20
): Promise<{
  users: any[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}/api/enterprise-hierarchy/users?page=${page}&limit=${limit}`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch users');
  }

  return data.data;
};

/**
 * Get aggregated assessment data for enterprise
 * Returns aggregated data from own enterprise or all sub-enterprises if parent
 */
export const getAggregatedAssessments = async (
  token: string
): Promise<{
  totalUsers: number;
  totalAssessments: number;
  averageScore: number;
  hierarchyLevel: number;
  isParent: boolean;
  subEnterpriseCount: number;
}> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}/api/enterprise-hierarchy/assessments`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch assessments');
  }

  return data.data;
};

/**
 * Get comprehensive enterprise overview
 * Reads all user data from enterprise and sub-branches
 * Returns aggregated assessment data from all sources
 */
export const getComprehensiveEnterpriseOverview = async (
  token: string,
  year?: number,
  mode: 'dass21' | 'overall' | 'sowb' = 'sowb'
): Promise<{
  success: boolean;
  data: {
    group: { id: string; groupName: string; createdBy: any };
    counts: { approvedMembers: number; withAssessments: number };
    averages: { 
      overall0to42?: number;
      depression0to42?: number;
      anxiety0to42?: number;
      stress0to42?: number;
      overall0to4?: number;
      attributes0to4?: Record<string, number>;
      attributesPercentage?: Record<string, number>;
    };
    distribution: { low: number; moderate: number; high: number };
    trends?: Record<string, any>;
  };
}> => {
  const params = new URLSearchParams();
  if (year) params.append('year', year.toString());
  params.append('mode', mode);
  const queryString = params.toString();

  const response = await fetch(
    `${API_CONFIG.BASE_URL}/api/enterprise-hierarchy/comprehensive-overview?${queryString}`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch comprehensive overview');
  }

  return {
    success: data.success,
    data: data.data
  };
};

